import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft } from 'lucide-react';

import SessionListView from '@/components/council/SessionListView';
import SessionPhaseBar from '@/components/council/SessionPhaseBar';
import Phase1StateOfField from '@/components/council/Phase1StateOfField';
import Phase2Triage from '@/components/council/Phase2Triage';
import Phase3Density from '@/components/council/Phase3Density';
import Phase4Resolution from '@/components/council/Phase4Resolution';
import Phase5Closing from '@/components/council/Phase5Closing';
import SessionSummaryView from '@/components/council/SessionSummaryView';
import SearchableTagSelect from '@/components/common/SearchableTagSelect';

import BriefingPacketGenerator from '@/components/council/BriefingPacketGenerator';
import CouncilAdvisorPanel from '@/components/council/CouncilAdvisorPanel';
import SessionSynthesisGenerator from '@/components/council/SessionSynthesisGenerator';

export default function HighCouncil() {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState('list'); // 'list', 'active', 'summary'
  const [activeSession, setActiveSession] = useState(null);
  const [viewingSession, setViewingSession] = useState(null);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [newSessionType, setNewSessionType] = useState('regular');
  const [newAttendees, setNewAttendees] = useState([]);

  const [editingSession, setEditingSession] = useState(null);
  const [editType, setEditType] = useState('regular');
  const [editAttendees, setEditAttendees] = useState([]);

  const { data: user } = useQuery({ queryKey: ['me'], queryFn: () => base44.auth.me() });
  const { data: sessions } = useQuery({
    queryKey: ['councilSessions'],
    queryFn: () => base44.entities.CouncilSession.list('-session_date'),
    initialData: []
  });
  const { data: profiles } = useQuery({
    queryKey: ['officerProfiles'],
    queryFn: () => base44.entities.OfficerProfile.list(),
    initialData: []
  });
  const currentProfile = profiles.find(p => p.user_email === user?.email);
  const { data: issues } = useQuery({ queryKey: ['issues'], queryFn: () => base44.entities.Issue.list(), initialData: [] });
  const { data: missions } = useQuery({ queryKey: ['missions'], queryFn: () => base44.entities.Mission.list(), initialData: [] });
  const { data: objectives } = useQuery({ queryKey: ['objectives'], queryFn: () => base44.entities.Objective.list(), initialData: [] });
  const { data: activities } = useQuery({ queryKey: ['activities'], queryFn: () => base44.entities.Activity.list(), initialData: [] });
  const { data: officers } = useQuery({ queryKey: ['officers'], queryFn: () => base44.entities.OfficerProfile.list(), initialData: [] });
  const { data: policies } = useQuery({ queryKey: ['policies'], queryFn: () => base44.entities.Policy.list(), initialData: [] });
  const { data: capabilities } = useQuery({ queryKey: ['capabilities'], queryFn: () => base44.entities.Capability.list(), initialData: [] });

  const createSessionMutation = useMutation({
    mutationFn: (data) => base44.entities.CouncilSession.create(data),
    onSuccess: (newSession) => {
      queryClient.invalidateQueries({ queryKey: ['councilSessions'] });
      setActiveSession(newSession);
      setMode('active');
      setShowNewDialog(false);
    }
  });

  const updateSessionMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.CouncilSession.update(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['councilSessions'] })
  });

  const deleteSessionMutation = useMutation({
    mutationFn: (id) => base44.entities.CouncilSession.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['councilSessions'] })
  });

  const handleEditSession = (session) => {
    setEditingSession(session);
    setEditType(session.session_type || 'regular');
    setEditAttendees(session.attendees || []);
  };

  const handleSaveEdit = () => {
    updateSessionMutation.mutate({
      id: editingSession.id,
      data: { session_type: editType, attendees: editAttendees }
    });
    setEditingSession(null);
  };

  const handleArchiveSession = (session) => {
    updateSessionMutation.mutate({ id: session.id, data: { status: 'completed', session_notes: (session.session_notes || '') + '\n[Archived by Commander in Chief]' } });
  };

  const handleDeleteSession = (session) => {
    deleteSessionMutation.mutate(session.id);
  };

  const handleStartNew = () => {
    setShowNewDialog(true);
  };

  const handleCreateSession = () => {
    const sessionId = `HC-${Date.now().toString(36).toUpperCase()}`;
    createSessionMutation.mutate({
      session_id: sessionId,
      session_date: new Date().toISOString(),
      session_type: newSessionType,
      status: 'in_progress',
      facilitator: user?.email,
      attendees: newAttendees,
      current_phase: 1,
      issues_reviewed: [],
      noise_count: 0,
      signal_count: 0,
      balloon_count: 0,
      rock_count: 0
    });
  };

  const handleResume = (session) => {
    setActiveSession(session);
    setMode('active');
  };

  const handleView = (session) => {
    setViewingSession(session);
    setMode('summary');
  };

  // Auto-save phase advancement
  const advancePhase = useCallback((nextPhase) => {
    if (!activeSession) return;
    const phaseKey = `phase${activeSession.current_phase}_completed_at`;
    
    // Recalculate counts from current issues
    const reviewedIssueIds = activeSession.issues_reviewed || [];
    const reviewedIssues = issues.filter(i => reviewedIssueIds.includes(i.id));
    const noiseCount = reviewedIssues.filter(i => i.classification_gate1 === 'noise').length;
    const signalCount = reviewedIssues.filter(i => i.classification_gate1 === 'signal').length;
    const balloonCount = reviewedIssues.filter(i => i.classification_gate2 === 'balloon').length;
    const rockCount = reviewedIssues.filter(i => i.classification_gate2 === 'rock').length;

    const updateData = {
      current_phase: nextPhase,
      [phaseKey]: new Date().toISOString(),
      noise_count: noiseCount,
      signal_count: signalCount,
      balloon_count: balloonCount,
      rock_count: rockCount
    };
    updateSessionMutation.mutate({ id: activeSession.id, data: updateData });
    setActiveSession(prev => ({ ...prev, ...updateData }));
  }, [activeSession, issues, updateSessionMutation]);

  const handleIssueClassified = useCallback((issueId) => {
    if (!activeSession) return;
    const existing = activeSession.issues_reviewed || [];
    if (!existing.includes(issueId)) {
      const updated = [...existing, issueId];
      updateSessionMutation.mutate({ id: activeSession.id, data: { issues_reviewed: updated } });
      setActiveSession(prev => ({ ...prev, issues_reviewed: updated }));
    }
    queryClient.invalidateQueries({ queryKey: ['issues'] });
  }, [activeSession, updateSessionMutation, queryClient]);

  const handleCompleteSession = useCallback((closingData) => {
    if (!activeSession) return;
    const finalData = {
      ...closingData,
      current_phase: 5,
      phase5_completed_at: new Date().toISOString(),
      status: 'completed'
    };
    updateSessionMutation.mutate({ id: activeSession.id, data: finalData });
    setActiveSession(null);
    setMode('list');
  }, [activeSession, updateSessionMutation]);

  const handleBackToList = () => {
    setMode('list');
    setActiveSession(null);
    setViewingSession(null);
  };

  // Get issues relevant to current session
  const sessionIssues = activeSession
    ? issues.filter(i => (activeSession.issues_reviewed || []).includes(i.id))
    : [];

  // All pending issues for triage (not yet reviewed + reviewed in this session)
  const allPendingIssues = issues.filter(i => 
    (!i.classification_gate1 || i.classification_gate1 === 'pending') ||
    (activeSession?.issues_reviewed || []).includes(i.id)
  );

  // For Phase 2: only show pending triage + already classified in this session
  const triageIssues = issues.filter(i => 
    (!i.classification_gate1 || i.classification_gate1 === 'pending') ||
    ((activeSession?.issues_reviewed || []).includes(i.id) && i.classification_gate1 && i.classification_gate1 !== 'pending')
  );

  // For Phase 3: signals from this session
  const densityIssues = issues.filter(i =>
    (activeSession?.issues_reviewed || []).includes(i.id) && i.classification_gate1 === 'signal'
  );

  // For Phase 4: classified density from this session
  const resolutionIssues = issues.filter(i =>
    (activeSession?.issues_reviewed || []).includes(i.id) && 
    i.classification_gate1 === 'signal' && 
    i.classification_gate2 && i.classification_gate2 !== 'pending'
  );

  const lastSession = sessions.find(s => s.status === 'completed' && s.id !== activeSession?.id);
  const currentPhase = activeSession?.current_phase || 1;

  const officerOptions = officers.map(o => ({ value: o.user_email, label: `${o.callsign} (${o.rank_title})` }));

  if (mode === 'summary' && viewingSession) {
    return <SessionSummaryView session={viewingSession} officers={officers} onBack={handleBackToList} />;
  }

  if (mode === 'active' && activeSession) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button data-testid="hc-exit-session-btn" variant="ghost" onClick={handleBackToList} className="text-slate-400 hover:text-slate-200">
            <ArrowLeft className="w-4 h-4 mr-2" /> Exit Session
          </Button>
          <span className="text-xs text-slate-500 font-mono">{activeSession.session_id}</span>
        </div>

        <SessionPhaseBar currentPhase={currentPhase} session={activeSession} />

        <div className="max-w-4xl mx-auto">
          {currentPhase === 1 && (
            <div className="space-y-4">
            <BriefingPacketGenerator session={activeSession} />
            <Phase1StateOfField
              issues={issues}
              allIssues={issues}
              missions={missions}
              officers={officers}
              lastSession={lastSession}
              onProceed={() => advancePhase(2)}
            />
            </div>
          )}
          {currentPhase === 2 && (
            <Phase2Triage
              issues={triageIssues}
              missions={missions}
              objectives={objectives}
              activities={activities}
              officers={officers}
              onProceed={() => advancePhase(3)}
              onIssueClassified={handleIssueClassified}
            />
          )}
          {currentPhase === 3 && (
            <Phase3Density
              issues={densityIssues}
              officers={officers}
              onProceed={() => advancePhase(4)}
              onIssueClassified={handleIssueClassified}
            />
          )}
          {currentPhase === 4 && (
            <Phase4Resolution
              issues={resolutionIssues}
              officers={officers}
              policies={policies}
              capabilities={capabilities}
              objectives={objectives}
              activities={activities}
              userProfile={currentProfile}
              onProceed={() => advancePhase(5)}
            />
          )}
          {currentPhase === 5 && (
            <div className="space-y-4">
            <Phase5Closing
              session={activeSession}
              sessionIssues={issues.filter(i => (activeSession.issues_reviewed || []).includes(i.id))}
              officers={officers}
              onComplete={handleCompleteSession}
            />
            <SessionSynthesisGenerator
              session={activeSession}
              sessionIssues={issues.filter(i => (activeSession.issues_reviewed || []).includes(i.id))}
            />
            </div>
          )}
        </div>

        {/* Floating AI Advisor — available on all phases */}
        <CouncilAdvisorPanel
          sessionId={activeSession.id}
          currentPhase={currentPhase}
          currentIssueId={null}
        />
      </div>
    );
  }

  return (
    <>
      <SessionListView
        sessions={sessions}
        officers={officers}
        onStartNew={handleStartNew}
        onResume={handleResume}
        onView={handleView}
        onEdit={handleEditSession}
        onArchive={handleArchiveSession}
        onDelete={handleDeleteSession}
        userProfile={currentProfile}
      />

      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent data-testid="hc-highcouncil-dialog" className="bg-slate-900 border-slate-800 text-slate-100">
          <DialogHeader>
            <DialogTitle>Start New High Council Session</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Session Type</Label>
              <Select value={newSessionType} onValueChange={setNewSessionType}>
                <SelectTrigger className="bg-slate-950 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="regular">Regular</SelectItem>
                  <SelectItem value="emergency">Emergency</SelectItem>
                  <SelectItem value="backlog_clearing">Backlog Clearing</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Attendees</Label>
              <SearchableTagSelect
                options={officerOptions}
                value={newAttendees}
                onValueChange={setNewAttendees}
                multiple
                placeholder="Select attendees..."
                emptyMessage="No officers."
              />
            </div>
            <Button onClick={handleCreateSession} disabled={createSessionMutation.isPending} className="w-full bg-indigo-600 hover:bg-indigo-700">
              {createSessionMutation.isPending ? 'Creating...' : 'Begin Session'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingSession} onOpenChange={(open) => !open && setEditingSession(null)}>
        <DialogContent data-testid="hc-highcouncil-dialog-1" className="bg-slate-900 border-slate-800 text-slate-100">
          <DialogHeader>
            <DialogTitle>Edit Session</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Session Type</Label>
              <Select value={editType} onValueChange={setEditType}>
                <SelectTrigger className="bg-slate-950 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="regular">Regular</SelectItem>
                  <SelectItem value="emergency">Emergency</SelectItem>
                  <SelectItem value="backlog_clearing">Backlog Clearing</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Attendees</Label>
              <SearchableTagSelect
                options={officerOptions}
                value={editAttendees}
                onValueChange={setEditAttendees}
                multiple
                placeholder="Select attendees..."
                emptyMessage="No officers."
              />
            </div>
            <Button onClick={handleSaveEdit} disabled={updateSessionMutation.isPending} className="w-full bg-indigo-600 hover:bg-indigo-700">
              {updateSessionMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}